import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RecordNav } from "../components/RecordNav";

const codeKey = (c) => c.code || c.name;
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, FloppyDisk, CaretDown, CaretRight, Trash, FileText, ListChecks, Note as NoteIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { useAutoSave, AutoSaveIndicator } from "../lib/useAutoSave";
import { StatusChip, ConfirmDialog } from "../components/shared";
import { AddressFields } from "../components/AddressFields";
import { NotesPane } from "../components/NotesPane";
import { ContactsPane } from "../components/ContactsPane";
import { usePerms } from "../lib/perms";
import { ViewOnlyBadge } from "../components/Permission";
import { isMobileViewport } from "../components/LineItemText";
import { suggestCode, CURRENCIES } from "../lib/constants";
import { DocLanguageSelect } from "../components/DocLanguageSelect";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

function Section({ title, children, grid = true, defaultOpen = true, mobileCollapsed = false }) {
  const [open, setOpen] = useState(defaultOpen && !(mobileCollapsed && isMobileViewport()));
  const key = title.toLowerCase().replace(/[^a-z]+/g, "-");
  return (
    <div className="border border-border bg-card mb-4" data-testid={`vsection-${key}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-secondary/50 transition-colors" data-testid={`vsection-toggle-${key}`}>
        {open ? <CaretDown size={16} className="text-primary" /> : <CaretRight size={16} className="text-muted-foreground" />}
        <span className="font-bold text-sm tracking-tight">{title}</span>
      </button>
      {open && <div className={`px-4 pb-4 pt-1 ${grid ? "grid grid-cols-2 gap-3 md:gap-4" : ""}`}>{children}</div>}
    </div>
  );
}
function F({ label, children }) {
  return (<div><Label className="overline-label">{label}</Label><div className="mt-1">{children}</div></div>);
}
function Row({ label, value, mono, tabular, bold }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground text-xs uppercase tracking-wide">{label}</span>
      <span className={`${mono ? "font-mono text-xs" : ""} ${tabular ? "tabular" : ""} ${bold ? "font-bold text-primary" : "font-medium"}`}>{value}</span>
    </div>
  );
}

const EMPTY = {
  code: "", name: "", email: "", phone: "", address: "", address2: "", city: "",
  state: "", postcode: "", country: "", currency: "", balance: 0,
};

export default function VendorCard() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: vendor, isLoading } = useQuery({ queryKey: ["vendor", id], queryFn: async () => (await api.get(`/vendors/${id}`)).data, enabled: !isNew });
  const { data: purchaseDocs = [] } = useQuery({ queryKey: ["purchase-orders"], queryFn: async () => (await api.get("/purchase-orders")).data, enabled: !isNew });

  const [form, setForm] = useState(EMPTY);
  const [codeEdited, setCodeEdited] = useState(false);
  useEffect(() => { if (vendor) { setForm({ ...EMPTY, ...vendor }); setCodeEdited(true); } }, [vendor]);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onName = (val) => setForm((f) => ({ ...f, name: val, ...(!codeEdited && isNew ? { code: suggestCode(val) } : {}) }));

  const docs = purchaseDocs.filter((d) => d.vendor_id === id).slice(0, 10);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["vendors"] }); qc.invalidateQueries({ queryKey: ["vendor", id] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };
  const payload = () => ({
    code: form.code, name: form.name, email: form.email, phone: form.phone,
    address: form.address, address2: form.address2, city: form.city, state: form.state,
    postcode: form.postcode, country: form.country,
    currency: form.currency || "", language: form.language || "en",
  });
  const save = useMutation({
    mutationFn: async () => isNew ? (await api.post("/vendors", payload())).data : (await api.put(`/vendors/${id}`, payload())).data,
    onSuccess: (res) => { invalidate(); toast.success(isNew ? "Vendor created" : "Vendor saved"); if (isNew) navigate(`/purchasing/vendor/${res.id}`, { replace: true }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const [delOpen, setDelOpen] = useState(false);
  const del = useMutation({ mutationFn: async () => api.delete(`/vendors/${id}`), onSuccess: () => { invalidate(); toast.success("Vendor deleted"); navigate("/purchasing?tab=vendors"); } });

  const createdRef = useRef(false);
  const autoSaveFn = async () => {
    if (isNew) {
      if (createdRef.current) return;
      createdRef.current = true;
      try { const res = (await api.post("/vendors", payload())).data; invalidate(); navigate(`/purchasing/vendor/${res.id}`, { replace: true }); return res; }
      catch (e) { createdRef.current = false; throw e; }
    }
    const res = (await api.put(`/vendors/${id}`, payload())).data; invalidate(); return res;
  };
  const { can } = usePerms();
  const viewOnly = !can("vendors.edit");
  const { status: autoStatus, flush: autoSaveFlush } = useAutoSave({ active: !viewOnly, canSave: !!form.name, createMode: isNew, snapshot: payload(), save: autoSaveFn });

  if (!isNew && isLoading) return <div className="flex items-center justify-center py-24"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-up pb-10" data-testid="vendor-card" onBlur={autoSaveFlush}>
      <div className="flex items-center gap-3 mb-1">
        <button onClick={() => navigate("/purchasing?tab=vendors")} className="p-1.5 -ml-1.5 text-muted-foreground hover:text-primary" data-testid="vendor-card-back"><ArrowLeft size={18} weight="bold" /></button>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="vendor-card-title">{isNew ? "New Vendor" : `${vendor.code} · ${vendor.name}`}</h1>
        {!isNew && Number(form.balance) > 0 && <StatusChip status="Open" />}
        <ViewOnlyBadge show={viewOnly && !isNew} />
        {!isNew && <div className="ml-auto"><RecordNav source="vendors" currentId={id} sortKey={codeKey} basePath="/purchasing/vendor" testId="vendor-nav" /></div>}
      </div>

      <div className="flex items-center gap-1 flex-wrap border border-border bg-card px-2 py-1.5 mb-5 rounded-sm">
        {!viewOnly && <button onMouseDown={(e) => e.preventDefault()} onClick={() => save.mutate()} disabled={!form.name || save.isPending} data-testid="vendor-ribbon-save" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"><FloppyDisk size={15} weight="bold" /> {isNew ? "Create" : "Save"}</button>}
        {!viewOnly && <AutoSaveIndicator status={autoStatus} isNew={isNew} testid="vendor-autosave" />}
        {!isNew && can("purchasing.delete") && <button onClick={() => setDelOpen(true)} data-testid="vendor-ribbon-delete" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold text-foreground hover:bg-secondary"><Trash size={15} weight="bold" /> Delete</button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <fieldset disabled={viewOnly} className="lg:col-span-2 min-w-0" data-testid="vendor-form-fields">
          <Section title="General">
            <F label="Code"><Input data-testid="vendor-code" value={form.code || ""} onChange={(e) => { setCodeEdited(true); setF("code", e.target.value.toUpperCase()); }} placeholder="Auto" className="rounded-sm font-mono" /></F>
            <F label="Name"><Input data-testid="vendor-name" value={form.name} onChange={(e) => onName(e.target.value)} className="rounded-sm" /></F>
            <F label="Currency">
              <Select value={form.currency || "none"} onValueChange={(v) => setF("currency", v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="vendor-currency"><SelectValue placeholder="Select currency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Document language">
              <DocLanguageSelect value={form.language} onChange={(v) => setF("language", v)} testId="vendor-language" />
            </F>
          </Section>

          <Section title="Address & Contact" grid={false}>
            <AddressFields values={form} onChange={(f, val) => setF(f, val)} testidPrefix="vendor" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><Label className="overline-label">Email</Label><Input data-testid="vendor-email" value={form.email} onChange={(e) => setF("email", e.target.value)} className="mt-1 rounded-sm" /></div>
              <div><Label className="overline-label">Phone</Label><Input data-testid="vendor-phone" value={form.phone} onChange={(e) => setF("phone", e.target.value)} className="mt-1 rounded-sm" /></div>
            </div>
          </Section>

          <Section title="Contacts" grid={false}>
            <ContactsPane basePath={`/vendors/${id}`} isNew={isNew} readOnly={viewOnly} contacts={vendor?.contacts || []} testidPrefix="vendor" onChanged={() => qc.invalidateQueries({ queryKey: ["vendor", id] })} />
          </Section>

          <Section title="Payables">
            <F label="Balance Owed (LCY)"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular" data-testid="vendor-balance">{money(form.balance)}</div></F>
          </Section>
        </fieldset>

        <div className="lg:col-span-1">
          <div className="border border-border bg-card sticky top-4" data-testid="vendor-info-pane">
            <Tabs defaultValue="details">
              <TabsList className="w-full rounded-none border-b border-border bg-secondary/40 h-auto p-0">
                <TabsTrigger value="details" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card"><ListChecks size={14} className="mr-1" /> Details</TabsTrigger>
                <TabsTrigger value="documents" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card"><FileText size={14} className="mr-1" /> Documents</TabsTrigger>
                <TabsTrigger value="notes" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card"><NoteIcon size={14} className="mr-1" /> Notes</TabsTrigger>
              </TabsList>
              <TabsContent value="details" className="p-4 space-y-2.5 text-sm mt-0">
                <Row label="Code" value={isNew ? "—" : form.code} mono />
                <Row label="Country" value={form.country || "—"} />
                <Row label="Currency" value={form.currency || "—"} />
                <div className="border-t border-border pt-2.5 space-y-2.5">
                  <Row label="Balance Owed" value={money(form.balance)} tabular bold />
                </div>
              </TabsContent>
              <TabsContent value="documents" className="p-4 mt-0">
                {isNew ? (
                  <p className="text-sm text-muted-foreground">Save the vendor to see their documents.</p>
                ) : docs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No purchase documents yet.</p>
                ) : (
                  <div className="space-y-1.5" data-testid="vendor-documents-list">
                    {docs.map((d) => (
                      <button key={d.id} onClick={() => navigate(`/purchasing/doc/${d.id}?tab=vendors`)} data-testid="vendor-doc-link" className="w-full flex items-center justify-between gap-2 border border-border rounded-sm px-2.5 py-2 text-left hover:bg-secondary/60 transition-colors">
                        <div className="min-w-0">
                          <p className="font-mono text-xs font-medium truncate">{d.doc_no}</p>
                          <p className="text-[11px] text-muted-foreground">{d.date}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <StatusChip status={d.status} />
                          <span className="tabular text-xs font-medium">{money(d.total)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="notes" className="p-4 mt-0">
                <NotesPane basePath={`/vendors/${id}`} isNew={isNew} readOnly={viewOnly} notes={vendor?.notes || []} testidPrefix="vendor" onChanged={() => qc.invalidateQueries({ queryKey: ["vendor", id] })} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <ConfirmDialog open={delOpen} onOpenChange={setDelOpen} title="Delete this vendor?" description={`${form.name} will be permanently deleted.`} confirmLabel="Delete" testid="vendor-card-delete-confirm" onConfirm={() => del.mutate()} />
    </div>
  );
}
